// ================================================================
// 👥 TEAMS REPORT - Relatório de Times & Perfis Operacionais
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const TeamsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamsData, setTeamsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsGridColor = useChartsColors();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: {
          color: chartsGridColor
        }
      },
      y: {
        grid: {
          color: chartsGridColor
        },
      }
    }
  };
  const loadTeamsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('👥 Carregando Relatório de Times com filtros...', filtersToUse);

      const result = await ReportsService.getTeamsReport({
        period: filtersToUse.period
      });

      if (result.success) {
        setTeamsData(result.data);
        console.log('✅ Relatório de Times carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeamsReport = useCallback(async () => {
    loadTeamsReportWithFilters(filters);
  }, [filters, loadTeamsReportWithFilters]);

  useEffect(() => {
    loadTeamsReport();
  }, [loadTeamsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadTeamsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadTeamsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const teamActivityData = teamsData?.charts?.teamActivity ? {
    labels: teamsData.charts.teamActivity.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Tickets Criados',
      data: teamsData.charts.teamActivity.map(item => item.tickets_created),
      borderColor: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }, {
      label: 'Tickets Resolvidos',
      data: teamsData.charts.teamActivity.map(item => item.tickets_resolved),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const performanceByRoleData = teamsData?.charts?.performanceByRole ? {
    labels: teamsData.charts.performanceByRole.map(item => item.role_name),
    datasets: [{
      label: 'Taxa de Resolução (%)',
      data: teamsData.charts.performanceByRole.map(item => item.resolution_rate),
      backgroundColor: ['#007bff', '#28a745', '#ffc107', '#17a2b8'],
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  const performanceColumns = teamsData?.charts?.performanceByRole ? [
    {
      key: 'role_name',
      label: 'Perfil',
      sortable: true
    },
    {
      key: 'member_count',
      label: 'Membros',
      sortable: true
    },
    {
      key: 'tickets_handled',
      label: 'Tickets Atendidos',
      sortable: true
    },
    {
      key: 'tickets_resolved',
      label: 'Tickets Resolvidos',
      sortable: true
    },
    {
      key: 'resolution_rate',
      label: 'Taxa de Resolução (%)',
      sortable: true,
      render: (value) => `${parseFloat(value).toFixed(2)}%`
    }
  ] : [];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:account-group" style={{ fontSize: '22px', color: '#007bff' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Times & Perfis Operacionais
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadTeamsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadTeamsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados do time...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && teamsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={teamsData.kpis.total_team_members.label}
                  value={teamsData.kpis.total_team_members.value}
                  icon="mdi:account-group"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={teamsData.kpis.active_collaborators.label}
                  value={teamsData.kpis.active_collaborators.value}
                  icon="mdi:account-check"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={teamsData.kpis.total_tickets.label}
                  value={teamsData.kpis.total_tickets.value}
                  icon="mdi:ticket-confirmation"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={teamsData.kpis.avg_sla.label}
                  value={teamsData.kpis.avg_sla.formatted}
                  icon="mdi:clock-fast"
                  color="#ffc107"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Atividade do Time
                    </span>
                    <Line data={teamActivityData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Performance por Perfil
                    </span>
                    <Bar data={performanceByRoleData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Performance Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Performance por Perfil
                </span>
                <SmartTable
                  columns={performanceColumns}
                  data={teamsData.charts.performanceByRole || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default TeamsReport;

